import { useState } from "react";
import { NavLink } from "react-router-dom";
import { ArrowUp, ArrowDown, ChevronsUp, ArrowUpDown, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { deplacer, enregistrerMenu, ordonner, useDisposition } from "@/lib/disposition";

const Btn = ({ onClick, disabled, title, testId, children, clair }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={title} aria-label={title} data-testid={testId}
    className={`grid h-6 w-6 shrink-0 place-items-center rounded transition-colors disabled:opacity-25 ${clair ? "text-slate-500 hover:bg-slate-200 hover:text-slate-900" : "text-slate-400 hover:bg-white/10 hover:text-white"}`}>
    {children}
  </button>
);

export const OrdreBoutons = ({ i, n, onMove, testId, clair = false, horizontal = false }) => (
  <span className="flex shrink-0 items-center" data-testid={testId}>
    <Btn onClick={() => onMove(i, 0)} disabled={i === 0} title="Mettre en haut" testId={`${testId}-haut`} clair={clair}><ChevronsUp className={`h-3.5 w-3.5 ${horizontal ? "-rotate-90" : ""}`} /></Btn>
    <Btn onClick={() => onMove(i, i - 1)} disabled={i === 0} title="Monter" testId={`${testId}-monter`} clair={clair}><ArrowUp className={`h-3.5 w-3.5 ${horizontal ? "-rotate-90" : ""}`} /></Btn>
    <Btn onClick={() => onMove(i, i + 1)} disabled={i === n - 1} title="Descendre" testId={`${testId}-descendre`} clair={clair}><ArrowDown className={`h-3.5 w-3.5 ${horizontal ? "-rotate-90" : ""}`} /></Btn>
  </span>
);

const useMenu = (nav) => {
  const d = useDisposition();
  const items = ordonner(nav, d?.menu || [], (x) => x.id);
  const bouger = (i, j) => { enregistrerMenu(deplacer(items.map((x) => x.id), i, j)).catch((e) => toast.error(errMsg(e))); };
  return { items, bouger };
};

export const MenuLateral = ({ nav, linkCls, compact = false }) => {
  const { items, bouger } = useMenu(nav);
  const [org, setOrg] = useState(false);
  if (compact) {
    return (
      <nav className="sidebar-scroll mt-4 flex min-h-0 flex-1 flex-col items-center gap-1 overflow-auto pb-2" data-testid="sidebar-nav">
        {items.map((n) => (
          <NavLink key={n.id} to={n.to} end={n.to === "/"} title={n.label} aria-label={n.label}
            className={(p) => `${linkCls(p)} justify-center !px-2.5`} data-testid={`nav-${n.id}`}>
            <n.icon className="h-4 w-4 shrink-0" />
          </NavLink>
        ))}
      </nav>
    );
  }
  return (
    <>
      <nav className="sidebar-scroll mt-4 -mr-2 flex min-h-0 flex-1 flex-col gap-1 overflow-auto pb-2 pr-2" data-testid="sidebar-nav">
        {items.map((n, i) => (
          <div key={n.id} className="flex w-max min-w-full items-center gap-1">
            <NavLink to={n.to} end={n.to === "/"} className={(p) => `${linkCls(p)} flex-1 whitespace-nowrap`} data-testid={`nav-${n.id}`}>
              <n.icon className="h-4 w-4 shrink-0" />
              {n.label}
            </NavLink>
            {org && <OrdreBoutons i={i} n={items.length} onMove={bouger} testId={`menu-ordre-${n.id}`} />}
          </div>
        ))}
      </nav>
      <div className="mt-2 flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <button type="button" onClick={() => setOrg(!org)} className={`flex items-center gap-1.5 transition-colors ${org ? "text-emerald-300" : "text-slate-500 hover:text-white"}`} data-testid="menu-organiser">
          <ArrowUpDown className="h-3.5 w-3.5" />{org ? "Terminer" : "Organiser le menu"}
        </button>
        {org && (
          <button type="button" onClick={() => { enregistrerMenu([]).catch((e) => toast.error(errMsg(e))); }} className="flex items-center gap-1.5 text-slate-500 hover:text-white" data-testid="menu-reinitialiser">
            <RotateCcw className="h-3.5 w-3.5" />Ordre d'origine
          </button>
        )}
      </div>
    </>
  );
};

export const MenuMobile = ({ nav, linkCls }) => {
  const { items } = useMenu(nav);
  return (
    <nav className="mt-3 flex gap-1 overflow-x-auto pb-1">
      {items.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkCls} data-testid={`mobile-nav-${n.id}`}>
          <n.icon className="h-4 w-4" />
          <span className="whitespace-nowrap">{n.label}</span>
        </NavLink>
      ))}
    </nav>
  );
};
